/**
 * Helper for grouping pending photos in the sidebar. Photos posted by the same user
 * for the same playground are presented as a single item, so they can be approved in
 * one go. The grouping is derived from the loaded data every time, nothing is stored,
 * which means a page reload always regroups whatever is still pending.
 */
let PhotoGroupHelper = {

    /**
     * Groups the photo items in the specified list of sidebar items. Photos by the same
     * user for the same playground are replaced by a single group item. Photos that end
     * up alone in a group are kept as they are, and all other items are passed through
     * untouched.
     *
     * @param {Array} items The sidebar items to group.
     * @returns {Array} A new list of items, where groupable photos have been grouped.
     */
    groupPhotoItems: function (items) {
        if (!items) {
            return [];
        }

        let grouped = [];
        let buckets = new Map();

        items.forEach(item => {
            if (!item.isPhoto) {
                grouped.push(item);
                return;
            }

            let key = this.getGroupKey(item);
            if (!buckets.has(key)) {
                buckets.set(key, []);
            }

            buckets.get(key).push(item);
        });

        buckets.forEach(photos => {
            if (photos.length == 1) {
                grouped.push(photos[0]);
                return;
            }

            grouped.push(this.makeGroup(photos));
        });

        return grouped;
    },

    /**
     * Gets the key used to decide which photos belong together: the user who posted the
     * photo and the playground it belongs to. Photos without a user are given a unique
     * key, so that they are never grouped with anything else.
     *
     * @param {Object} photo
     * @returns {String}
     */
    getGroupKey: function (photo) {
        if (!photo.user || !photo.playground) {
            return `photo-${photo._id}`;
        }

        return `${photo.user}|${photo.playground._id}`;
    },

    /**
     * Creates a sidebar item representing a group of photos.
     *
     * Note that the photo items are kept by reference, since approving or rejecting a
     * photo updates its state in place.
     *
     * @param {Array} photos The photos of the group, at least one.
     * @returns {Object} The group item.
     */
    makeGroup: function (photos) {
        let dates = photos.map(p => p.created).sort();

        return {
            isPhotoGroup: true,

            // Group items have no _id of their own, so they are identified by tempId,
            // just like the dropped draft playgrounds are.
            tempId: `photogroup-${photos[0]._id}`,

            photos: photos,
            playground: photos[0].playground,
            user: photos[0].user,

            // Sort the group where its oldest photo would have ended up
            date: dates[0],
        };
    },

    /**
     * Sorts sidebar items by date, oldest first. The list is sorted in place.
     *
     * @param {Array} items
     * @returns {Array} The sorted list.
     */
    sortItems: function (items) {
        return items.sort((a, b) => {
            if (a.date < b.date) {
                return -1;
            }
            if (a.date > b.date) {
                return 1;
            }
            return 0;
        });
    }
}

export default PhotoGroupHelper;
